/**
 * SyncManager — drains the offline action queue whenever the app comes
 * back online. Mounted once at the top of the tree; renders nothing.
 *
 * Also exposes `queueAction()` and `getPendingCount()` as a lightweight
 * imperative API that other components can call when they know a request
 * failed due to the network. The API interceptor in `api.js` calls this
 * automatically for POST/PATCH/PUT/DELETE requests that fail with a
 * network error, so most components don't need to worry.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useOnline } from "@/lib/useOnline";
import { api } from "@/lib/api";
import { drainQueue, pendingCount, listQueuedActions } from "@/lib/offlineStore";

export function SyncManager() {
  const { online } = useOnline();
  const [prevOnline, setPrevOnline] = useState(online);
  const [pending, setPending] = useState(0);

  // Refresh pending count every 12 s + on any storage event.
  useEffect(() => {
    let killed = false;
    const tick = async () => {
      const n = await pendingCount().catch(() => 0);
      if (!killed) setPending(n);
    };
    tick();
    const id = setInterval(tick, 12_000);
    const onStorage = () => tick();
    window.addEventListener("focus", onStorage);
    return () => {
      killed = true;
      clearInterval(id);
      window.removeEventListener("focus", onStorage);
    };
  }, []);

  // Drain whenever we transition offline → online.
  useEffect(() => {
    if (online && !prevOnline) {
      (async () => {
        const before = await pendingCount().catch(() => 0);
        if (!before) { setPrevOnline(online); return; }
        // Explicit retry only — nothing is replayed without the user's tap.
        toast.info(`Back online — ${before} saved change${before === 1 ? "" : "s"} not sent yet`, {
          duration: 15000,
          action: {
            label: "Retry now",
            onClick: async () => {
              const results = await drainQueue(api).catch(() => null);
              setPending(await pendingCount().catch(() => 0));
              if (results?.drained) toast.success(`${results.drained} sent`);
              else if (results?.deferred || results?.failed) toast.warning("Some changes couldn't be sent. Try again.");
            },
          },
        });
      })();
    }
    setPrevOnline(online);
  }, [online, prevOnline]);

  return null;
}

/**
 * Public helper — components can call this when they detect a network
 * failure and want to persist the intent locally. Returns the queued row
 * so the caller can show an inline "Pending Sync" tag.
 */
export async function queueAction(payload) {
  const { enqueueAction } = await import("@/lib/offlineStore");
  return enqueueAction(payload);
}

/** Reactive hook — returns [pendingCount, allPending[]] for badge widgets.
 *  Also exposes `refresh()` for callers that just mutated the queue and want
 *  the UI to update immediately instead of waiting for the 15-s poll. */
export function usePendingSync() {
  const [count, setCount] = useState(0);
  const [rows,  setRows]  = useState([]);
  const tick = async () => {
    try {
      const list = await listQueuedActions("pending");
      setRows(list);
      setCount(list.length);
    } catch { /* silent */ }
  };
  useEffect(() => {
    let killed = false;
    const wrappedTick = async () => { if (!killed) await tick(); };
    wrappedTick();
    const id = setInterval(wrappedTick, 15_000);
    return () => { killed = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { count, rows, refresh: tick };
}
